/**
 * PDP main gallery: desktop hover zoom, click-to-fullscreen, and modal click-zoom.
 */
(() => {
  "use strict";

  const DESKTOP_MIN = 1024;
  const CAROUSEL_SELECTOR = '[id^="ProductMainImages-"]';
  const MODAL_CAROUSEL_SELECTOR = '[id^="ProductModalCarousel-"]';
  const TAP_THRESHOLD = 10;

  const canHoverZoom = () =>
    window.innerWidth >= DESKTOP_MIN &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const isIgnoredTarget = (target) => {
    if (!(target instanceof Element)) return true;
    return !!target.closest("button, a, video, iframe");
  };

  const openFullscreenModal = (carouselEl) => {
    const trigger = carouselEl.closest("s-carousel")?.querySelector("[js-zoom-trigger]");
    if (!trigger || carouselEl.dataset.zoomOpening === "true") return;

    carouselEl.dataset.zoomOpening = "true";

    // Fix for the modal seemingly not opening on click:
    // <main> is `.modal-background`. Opening the modal binds a close listener
    // there, so the originating mouse click would immediately close it.
    const swallowClick = (event) => {
      event.stopPropagation();
    };
    document.addEventListener("click", swallowClick, true);

    window.setTimeout(() => {
      document.removeEventListener("click", swallowClick, true);
      trigger.click();
      carouselEl.dataset.zoomOpening = "false";
    }, 0);
  };

  const resetAll = (carouselEl) => {
    carouselEl?.querySelectorAll(".js-hover-zoom.is-zooming").forEach((frame) => {
      frame.classList.remove("is-zooming");
    });
  };

  const ensureHighRes = (frame) => {
    const img = frame.querySelector("img");
    const zoomSrc = frame.getAttribute("data-zoom-src");
    if (!img || !zoomSrc || img.dataset.zoomLoaded === "true") return;

    img.src = zoomSrc;
    img.dataset.zoomLoaded = "true";
  };

  const updateZoom = (frame, event) => {
    const rect = frame.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    frame.style.setProperty("--zoom-x", `${((event.clientX - rect.left) / rect.width) * 100}%`);
    frame.style.setProperty("--zoom-y", `${((event.clientY - rect.top) / rect.height) * 100}%`);
    frame.classList.add("is-zooming");
  };

  const bindCarousel = (carouselEl) => {
    if (!carouselEl || carouselEl.dataset.hoverZoomBound === "true") return;
    carouselEl.dataset.hoverZoomBound = "true";

    let tapX = 0;
    let tapY = 0;

    carouselEl.addEventListener("pointerdown", (event) => {
      tapX = event.clientX;
      tapY = event.clientY;
    });

    carouselEl.addEventListener("pointerup", (event) => {
      if (Math.hypot(event.clientX - tapX, event.clientY - tapY) > TAP_THRESHOLD) return;

      const target = event.target;
      if (isIgnoredTarget(target)) return;

      if (target.closest(".js-click-fullscreen")) {
        openFullscreenModal(carouselEl);
        return;
      }

      if (window.innerWidth >= DESKTOP_MIN) return;
      if (!target.closest(".js-hover-zoom")) return;

      openFullscreenModal(carouselEl);
    });

    carouselEl.addEventListener("mousemove", (event) => {
      if (!canHoverZoom()) {
        resetAll(carouselEl);
        return;
      }

      const frame = event.target.closest(".js-hover-zoom");
      if (!frame) {
        resetAll(carouselEl);
        return;
      }

      ensureHighRes(frame);
      updateZoom(frame, event);
    });

    carouselEl.addEventListener("mouseleave", () => resetAll(carouselEl));

    const bindSlideReset = () => {
      const swiper = carouselEl.swiper;
      if (!swiper) return false;
      if (swiper.__hoverZoomBound) return true;

      swiper.__hoverZoomBound = true;
      swiper.on("slideChange", () => resetAll(carouselEl));
      return true;
    };

    if (!bindSlideReset()) {
      const timer = window.setInterval(() => {
        if (bindSlideReset()) window.clearInterval(timer);
      }, 100);
      window.setTimeout(() => window.clearInterval(timer), 5000);
    }
  };

  const syncModalCarouselSwipe = (swiper, scale) => {
    const isZoomed = (scale ?? swiper.zoom?.scale ?? 1) > 1;
    swiper.allowTouchMove = !isZoomed;

    if (swiper.mousewheel) {
      if (isZoomed) {
        swiper.mousewheel.disable();
      } else {
        swiper.mousewheel.enable();
      }
    }
  };

  const bindModalCarousel = (carouselEl) => {
    if (!carouselEl || carouselEl.dataset.modalZoomBound === "true") return;

    const bind = () => {
      const swiper = carouselEl.swiper;
      if (!swiper?.zoom) return false;

      carouselEl.dataset.modalZoomBound = "true";

      let tapX = 0;
      let tapY = 0;

      carouselEl.addEventListener("pointerdown", (event) => {
        tapX = event.clientX;
        tapY = event.clientY;
      });

      carouselEl.addEventListener("pointerup", (event) => {
        if (Math.hypot(event.clientX - tapX, event.clientY - tapY) > TAP_THRESHOLD) return;
        if (isIgnoredTarget(event.target)) return;
        if (!event.target.closest(".swiper-zoom-container")) return;

        swiper.zoom.toggle(event);
      });

      swiper.on("zoomChange", (_swiper, scale) => {
        syncModalCarouselSwipe(swiper, scale);
      });

      swiper.on("slideChange", () => {
        if (swiper.zoom?.scale && swiper.zoom.scale > 1) {
          swiper.zoom.out();
        }

        syncModalCarouselSwipe(swiper);
      });

      syncModalCarouselSwipe(swiper);

      return true;
    };

    if (!bind()) {
      const timer = window.setInterval(() => {
        if (bind()) window.clearInterval(timer);
      }, 100);
      window.setTimeout(() => window.clearInterval(timer), 5000);
    }
  };

  const init = () => {
    document.querySelectorAll(CAROUSEL_SELECTOR).forEach(bindCarousel);
    document.querySelectorAll(MODAL_CAROUSEL_SELECTOR).forEach(bindModalCarousel);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.addEventListener("resize", () => {
    if (!canHoverZoom()) document.querySelectorAll(CAROUSEL_SELECTOR).forEach(resetAll);
  });
})();
